import { useState } from "react";
import { Loader2, Pencil } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

const STATES = ["QLD", "NSW", "VIC", "SA", "WA", "TAS", "NT", "ACT"];
const OCCUPANCY = [
  { v: "", label: "Not answered" },
  { v: "owner", label: "Owner occupier" },
  { v: "tenant", label: "Tenant" },
  { v: "other", label: "Other" },
];

// The public QR form captures ONE name box, so full names routinely land
// in first_name ("David Smith"). Split on save so the greeting in emails
// and the ServiceM8 contact are right, without touching the public form.
function splitName(first, last) {
  if (last) return [first || "", last];
  const parts = (first || "").trim().split(/\s+/);
  if (parts.length < 2) return [first || "", ""];
  return [parts[0], parts.slice(1).join(" ")];
}

const Field = ({ label, children, hint }) => (
  <label className="block">
    <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{label}</span>
    {children}
    {hint && <span className="mt-0.5 block text-[10px] text-slate-400">{hint}</span>}
  </label>
);

const inputCls =
  "mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-cyan-500 focus:outline-none";

/**
 * Admin correction of a captured lead. `invite` is the invite row;
 * `hasAppointment` drives the "this also updates the booking + ServiceM8
 * job" copy so the admin knows how far the change travels.
 */
export function EditLeadDialog({ invite, hasAppointment = false, onSaved, trigger }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [notify, setNotify] = useState(false);
  const initial = () => {
    const [first, last] = splitName(invite.first_name, invite.last_name);
    return {
      first_name: first, last_name: last,
      mobile: invite.mobile || "", email: invite.email || "",
      street_address: invite.street_address || "", suburb: invite.suburb || "",
      state: invite.state || "QLD", postcode: invite.postcode || "",
      occupancy_status: invite.occupancy_status || "", notes: invite.notes || "",
    };
  };
  const [f, setF] = useState(initial);
  const set = (k) => (e) => setF((c) => ({ ...c, [k]: e.target.value }));
  // The Upcoming list passes an APPOINTMENT row, which has no notes /
  // occupancy columns. Sending "" for a field the caller never had would
  // wipe good data on the invite, so anything absent from the source is
  // hidden and never submitted.
  const absent = (k) => invite[k] === undefined;

  const openDialog = () => { setF(initial()); setNotify(false); setOpen(true); };

  const save = async () => {
    if (busy) return;
    setBusy(true);
    const payload = { notify_customer: notify };
    Object.entries(f).forEach(([k, v]) => { if (!absent(k)) payload[k] = v; });
    try {
      const { data } = await api.patch(
        `/admin/property-campaign-invites/${invite.id}`, payload);
      const n = Object.keys(data.changed || {}).filter((k) => k !== "mobile_e164").length;
      if (!n) toast.info("No changes — the details already match.");
      else {
        const bits = [`${n} field${n === 1 ? "" : "s"} updated`];
        if (data.appointments_updated) bits.push(`${data.appointments_updated} booking updated`);
        const sm8Ok = (data.servicem8 || []).filter((r) => r.ok && !r.skipped).length;
        const sm8Bad = (data.servicem8 || []).filter((r) => !r.ok);
        if (sm8Ok) bits.push("ServiceM8 job updated");
        if (data.customer_notified) bits.push("customer emailed");
        toast.success(bits.join(" · "));
        if (sm8Bad.length) {
          toast.error(`Saved, but the ServiceM8 push failed: ${sm8Bad[0].error}. Use Re-sync on the booking.`);
        }
      }
      setOpen(false);
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save the details");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {trigger ? (
        <span onClick={openDialog} data-testid={`lead-edit-open-${invite.id}`}>{trigger}</span>
      ) : (
        <button
          type="button"
          onClick={openDialog}
          data-testid={`lead-edit-open-${invite.id}`}
          className="inline-flex items-center gap-1 text-xs font-semibold text-cyan-700 hover:text-cyan-900"
        >
          <Pencil className="w-3.5 h-3.5" /> Edit details
        </button>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="lead-edit-dialog">
          <DialogHeader>
            <DialogTitle>Edit customer &amp; property details</DialogTitle>
          </DialogHeader>

          <p className="text-xs text-slate-500">
            {hasAppointment
              ? "Corrections also update the booking the technician sees and the linked ServiceM8 job."
              : "No booking yet — this updates the lead record only."}
          </p>

          <div className="grid grid-cols-2 gap-3 mt-2">
            <Field label="First name">
              <input className={inputCls} value={f.first_name} onChange={set("first_name")}
                data-testid="lead-edit-first-name" />
            </Field>
            <Field label="Last name">
              <input className={inputCls} value={f.last_name} onChange={set("last_name")}
                data-testid="lead-edit-last-name" />
            </Field>
            <Field label="Mobile">
              <input className={inputCls} value={f.mobile} onChange={set("mobile")}
                placeholder="04xx xxx xxx" data-testid="lead-edit-mobile" />
            </Field>
            <Field label="Email">
              <input className={inputCls} value={f.email} onChange={set("email")}
                data-testid="lead-edit-email" />
            </Field>
          </div>

          <div className="mt-3 grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Field label="Street address">
                <input className={inputCls} value={f.street_address} onChange={set("street_address")}
                  data-testid="lead-edit-street" />
              </Field>
            </div>
            <Field label="Suburb">
              <input className={inputCls} value={f.suburb} onChange={set("suburb")}
                data-testid="lead-edit-suburb" />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="State">
                <select className={inputCls} value={f.state} onChange={set("state")}
                  data-testid="lead-edit-state">
                  {STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </Field>
              <Field label="Postcode">
                <input className={inputCls} value={f.postcode} onChange={set("postcode")}
                  inputMode="numeric" maxLength={4} data-testid="lead-edit-postcode" />
              </Field>
            </div>
            <Field label="Occupancy">
              <select className={inputCls} value={f.occupancy_status} onChange={set("occupancy_status")}
                data-testid="lead-edit-occupancy" disabled={absent("occupancy_status")}>
                {OCCUPANCY.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
              </select>
            </Field>
            {!absent("notes") && (
              <div className="col-span-2">
                <Field label="Notes" hint="Internal only — never shown to the customer.">
                  <textarea className={`${inputCls} min-h-[60px]`} value={f.notes} onChange={set("notes")}
                    data-testid="lead-edit-notes" />
                </Field>
              </div>
            )}
          </div>

          {hasAppointment && (
            <label className="mt-3 flex items-start gap-2 rounded-lg bg-slate-50 border border-slate-200 p-3">
              <input type="checkbox" checked={notify} className="mt-0.5"
                onChange={(e) => setNotify(e.target.checked)}
                data-testid="lead-edit-notify" />
              <span className="text-xs text-slate-600">
                Email the customer an updated confirmation
                <span className="block text-[10px] text-slate-400">
                  Leave unticked for a silent typo fix.
                </span>
              </span>
            </label>
          )}

          <DialogFooter className="mt-4">
            <Button variant="outline" className="rounded-full" onClick={() => setOpen(false)}
              data-testid="lead-edit-cancel">Cancel</Button>
            <Button className="rounded-full bg-slate-900" onClick={save} disabled={busy}
              data-testid="lead-edit-save">
              {busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
              Save details
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default EditLeadDialog;
